import type { Claim } from '@/api/claims';
import type { ClientRecord, ClientInsurance } from '@/api/clients';
import type { DirectoryEntry } from '@/api/users';
import type { PracticeSettings } from '@/api/settings';

/*
 * Pre-flight validation for an 837P Professional claim.
 *
 * Pure, synchronous, no network. Consumes the already-loaded records that
 * BillingDetailPage has in hand and returns a list of issues the biller
 * can work through. Issues are classified `blocking` (OA will reject — do
 * not submit) or `warning` (OA might accept, but the payer frequently
 * doesn't; the user can override).
 *
 * The checks cover fields required to *assemble* a valid 837P payload at
 * HIPAA 5010 level. Real payer-specific requirements (e.g. Medicaid
 * taxonomy mandates, ordering-provider edits) are out of scope until
 * Phase 2.5.3 when we learn them from live OATEST + 277CA responses.
 */

export type ValidationSeverity = 'blocking' | 'warning';

export interface ValidationIssue {
    field: string;
    severity: ValidationSeverity;
    message: string;
}

interface RenderingStaff extends Pick<DirectoryEntry, 'first' | 'last' | 'roleName' | 'roleType'> {
    /** Rendering-provider taxonomy (users.taxonomy). */
    taxonomy?: string | null;
    /** Rendering-provider NPI (users.npi on the full user record). */
    npi?: string | null;
}

function isBlank(v: string | null | undefined): boolean {
    return !v || !String(v).trim();
}

function isValidNpi(v: string | null | undefined): boolean {
    return !!v && /^\d{10}$/.test(String(v).trim());
}

function isValidEin(v: string | null | undefined): boolean {
    // 9 digits, optional dash after the first two — matches XX-XXXXXXX or 9 digits.
    return !!v && /^\d{2}-?\d{7}$/.test(String(v).trim());
}

function isValidTaxonomy(v: string | null | undefined): boolean {
    return !!v && /^[0-9A-Za-z]{9,10}[Xx]?$/.test(String(v).trim());
}

function isValidZip(v: string | null | undefined): boolean {
    return !!v && /^\d{5}(-?\d{4})?$/.test(String(v).trim());
}

function isValidDate(v: string | null | undefined): boolean {
    return !!v && /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim());
}

function isValidIcd10(v: string | null | undefined): boolean {
    return !!v && /^[A-Z][0-9A-Z]{1,6}(\.[0-9A-Z]{1,4})?$/.test(String(v).trim());
}

export interface Validate837PInput {
    claim: Claim;
    client: ClientRecord;
    practice: PracticeSettings | undefined;
    /** The rendering staff's directory entry. `undefined` if unresolved. */
    staff: RenderingStaff | undefined;
}

export function validate837P({
    claim, client, practice, staff,
}: Validate837PInput): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    const add = (field: string, severity: ValidationSeverity, message: string) =>
        issues.push({ field, severity, message });

    // ─── Billing provider (Loop 2000A / 2010AA) ───────────────────────────
    if (!practice) {
        add('billingProvider', 'blocking', 'Practice settings not loaded.');
    } else {
        if (isBlank(practice.practiceName)) add('billingProvider.name', 'blocking', 'Billing provider name is required.');
        if (!isValidNpi(practice.npi))      add('billingProvider.npi', 'blocking', 'Billing provider NPI must be 10 digits.');
        if (!isValidEin(practice.ein))      add('billingProvider.ein', 'blocking', 'Billing provider tax ID (EIN) must be 9 digits.');
        if (isBlank(practice.address))      add('billingProvider.address', 'blocking', 'Billing provider address is required.');
        if (isBlank(practice.city))         add('billingProvider.city', 'blocking', 'Billing provider city is required.');
        if (isBlank(practice.state))        add('billingProvider.state', 'blocking', 'Billing provider state is required.');
        if (!isValidZip(practice.zip))      add('billingProvider.zip', 'blocking', 'Billing provider ZIP must be 5 or 9 digits.');
        if (!isValidTaxonomy(practice.taxonomy)) add('billingProvider.taxonomy', 'warning', 'Billing provider taxonomy missing — most payers require it.');
    }

    // ─── Rendering provider (Loop 2310B) ──────────────────────────────────
    if (!staff) {
        add('renderingProvider', 'blocking', 'Rendering provider not resolved.');
    } else {
        if (isBlank(staff.first) || isBlank(staff.last)) {
            add('renderingProvider.name', 'blocking', 'Rendering provider name is required.');
        }
        if (!isValidNpi(staff.npi)) {
            add('renderingProvider.npi', 'blocking', 'Rendering provider NPI must be 10 digits.');
        }
        if (!isValidTaxonomy(staff.taxonomy)) {
            add('renderingProvider.taxonomy', 'warning', 'Rendering provider taxonomy missing — most payers require it.');
        }
    }

    // ─── Patient (Loop 2010CA) ────────────────────────────────────────────
    if (isBlank(client.first) || isBlank(client.last)) add('patient.name', 'blocking', 'Patient first and last name are required.');
    if (!isValidDate(client.dob))                      add('patient.dob', 'blocking', 'Patient DOB must be YYYY-MM-DD.');
    if (isBlank(client.gender))                        add('patient.gender', 'blocking', 'Patient gender is required.');
    if (isBlank(client.address))                       add('patient.address', 'blocking', 'Patient address is required.');
    if (isBlank(client.city))                          add('patient.city', 'blocking', 'Patient city is required.');
    if (isBlank(client.state))                         add('patient.state', 'blocking', 'Patient state is required.');
    if (!isValidZip(client.zip))                       add('patient.zip', 'blocking', 'Patient ZIP must be 5 or 9 digits.');

    // ─── Subscriber (Loop 2010BA) ─────────────────────────────────────────
    const insRec: ClientInsurance = claim.primaryClaimId
        ? (client.ins2 ?? {}) as ClientInsurance
        : (client.ins1 ?? {}) as ClientInsurance;
    if (isBlank(insRec.payer))   add('subscriber.payer', 'blocking', 'Payer name is required.');
    if (isBlank(insRec.id))      add('subscriber.memberId', 'blocking', 'Subscriber member ID is required.');
    const rel = insRec.subRel ?? 'self';
    if (rel !== 'self') {
        if (isBlank(insRec.sub))         add('subscriber.name', 'blocking', 'Subscriber name is required when subscriber ≠ patient.');
        if (!isValidDate(insRec.subDob)) add('subscriber.dob', 'blocking', 'Subscriber DOB is required when subscriber ≠ patient.');
        if (isBlank(insRec.subGender))   add('subscriber.gender', 'blocking', 'Subscriber gender is required when subscriber ≠ patient.');
        const addr = insRec.subAddress ?? {};
        if (isBlank(addr.street))     add('subscriber.address', 'blocking', 'Subscriber address is required when subscriber ≠ patient.');
        if (isBlank(addr.city))       add('subscriber.city', 'blocking', 'Subscriber city is required when subscriber ≠ patient.');
        if (isBlank(addr.state))      add('subscriber.state', 'blocking', 'Subscriber state is required when subscriber ≠ patient.');
        if (!isValidZip(addr.zip))    add('subscriber.zip', 'blocking', 'Subscriber ZIP must be 5 or 9 digits when subscriber ≠ patient.');
    }

    // ─── Payer (Loop 2010BB) ──────────────────────────────────────────────
    if (isBlank(claim.payer))   add('payer.name', 'blocking', 'Payer name on the claim is required.');
    if (isBlank(claim.payerId)) add('payer.id', 'blocking', 'Payer ID (EDI) on the claim is required.');

    // ─── Claim (Loop 2300) ────────────────────────────────────────────────
    if (!isValidIcd10(claim.dx1))   add('claim.dx1', 'blocking', 'Primary diagnosis (ICD-10) is required and must be valid.');
    if (claim.dx2 && !isValidIcd10(claim.dx2)) add('claim.dx2', 'warning', 'Secondary diagnosis is not a valid ICD-10 format.');
    if (!isValidDate(claim.serviceDate)) add('claim.serviceDate', 'blocking', 'Service date must be YYYY-MM-DD.');
    if (isBlank(claim.authNumber) && isBlank(claim.referralNumber)) {
        add('claim.authOrReferral', 'warning', 'Neither authorization # nor referral # is set — most payers require one or the other.');
    }
    if (claim.frequency === 'corrected' || claim.frequency === 'void') {
        if (isBlank(claim.originalClaimNumber)) {
            add('claim.originalClaimNumber', 'blocking', `Original claim # is required when frequency is '${claim.frequency}'.`);
        }
    }

    // ─── Service line (Loop 2400) ─────────────────────────────────────────
    if (isBlank(claim.serviceCode)) add('line.serviceCode', 'blocking', 'CPT/HCPCS code is required.');
    if (isBlank(claim.pos))         add('line.pos', 'blocking', 'Place of Service code is required.');
    if (!claim.units || claim.units <= 0)       add('line.units', 'blocking', 'Units must be greater than zero.');
    if (!claim.unitRate || claim.unitRate <= 0) add('line.unitRate', 'blocking', 'Unit rate must be greater than zero.');
    if (!claim.amount || claim.amount <= 0)     add('line.amount', 'blocking', 'Billed amount must be greater than zero.');
    // Modifier presence varies by payer/code — not validated here beyond shape.
    for (const m of [claim.mod1, claim.mod2, claim.mod3, claim.mod4]) {
        if (m && m.trim() && !/^[A-Za-z0-9]{2}$/.test(m.trim())) {
            add('line.modifier', 'blocking', `Modifier "${m}" must be two alphanumerics.`);
        }
    }

    return issues;
}

export function hasBlockingIssues(issues: ValidationIssue[]): boolean {
    return issues.some((i) => i.severity === 'blocking');
}
